import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TrackingLinkModel, TrackingLinkRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['trackingLinks']

type TrackingLinkFilters = {
  owner_id?: number,
  owner_type?: string,
  recipient_id?: number,
  recipient_type?: string,
  subject_id?: number,
  subject_type?: string,
}

type UseTrackingLinksOptions = {
  filters?: TrackingLinkFilters,
  performHttpRequests?: boolean,
  requestOptions?: TrackingLinkRequestOptions,
}

function useTrackingLinks(options: UseTrackingLinksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    recipient_id: filterRecipientId,
    recipient_type: filterRecipientType,
    subject_id: filterSubjectId,
    subject_type: filterSubjectType,
  } = filters

  const {
    updatedEntities: { trackingLinks: trackingLinksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { trackingLinks } = useSelector(reduxState => reduxState.entities)

  const filteredTrackingLinks = useMemo(() => {
    const filtered = Object.values(trackingLinks).filter((trackingLink: TrackingLinkModel) => {
      const {
        owner_id, owner_type, recipient_id, recipient_type, subject_id, subject_type,
      } = trackingLink

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      const matchRecipientId = matchFilterNumber(recipient_id, filterRecipientId)
      const matchRecipientType = filterRecipientType ? recipient_type === filterRecipientType : true

      const matchSubjectId = matchFilterNumber(subject_id, filterSubjectId)
      const matchSubjectType = filterSubjectType ? subject_type === filterSubjectType : true

      return matchOwnerId
       && matchOwnerType
       && matchRecipientId
       && matchRecipientType
       && matchSubjectId
       && matchSubjectType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [trackingLinksUpdatedAt, JSON.stringify(filters)])

  const filteredTrackingLinksCount = filteredTrackingLinks.length
  const hasFilteredTrackingLinks = !!filteredTrackingLinksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTrackingLinksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTrackingLinks } = useReduxAction(
    'trackingLinks',
    'loadTrackingLinks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrackingLinks,
    filteredTrackingLinksCount,
    hasFilteredTrackingLinks,
    loading: loadingTrackingLinks,
  }
}

export default useTrackingLinks
